import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';

/**
 * 指定時間処理を待機する
 * @param ms 待つ時間（ミリ秒）
 * @returns
 */
export const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * オブジェクトが空かどうか判定
 * @param obj
 * @returns
 */
export const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};

/**
 * オブジェクトから指定したキーのみで新しいオブジェクトを作成
 */
export const pick = <
  T extends Record<string, unknown>,
  K extends [keyof T, ...(keyof T)[]],
>(
  obj: T,
  ...keys: K
): Pick<T, K[number]> => {
  const result = {} as Pick<T, K[number]>;
  for (const key of keys) {
    if (key in obj) result[key] = obj[key];
  }
  return result;
};

/**
 * URL文字列をパースしてオブジェクトに変換
 * ＠example "/sample/?key1=value1&key2=value2" => { path: "/sample/", queries: { key1: "value1", key2: "value2" } }
 */
export const getPathObject = (fullPath: string) => {
  const urlObj = new URL(`https://example.com${fullPath}`);
  const params = new URLSearchParams(urlObj.search);

  // クエリパラメータをオブジェクト形式に変換
  const queryParams = Array.from(params.entries()).reduce(
    (acc, [key, value]) => {
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  );
  return { path: urlObj.pathname, queries: queryParams };
};

/**
 * クラス名を結合する
 * @param inputs
 * @returns
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 配列の中からランダムで選択する
 * @param array
 * @returns
 */
export const choiceRandom = <T>(array: T[]): T => {
  const randomIndex = Math.floor(Math.random() * array.length);
  return array[randomIndex];
};

/**
 * 分割された日付をフォーマットする
 * @param inputSeparateDate 例: ['2025', '5', '8']
 * @param format 例: YYYY-MM-DD
 * @returns
 */
export const formatSeparateDate = (
  inputSeparateDate: (string | number)[] | undefined,
  format: string,
): string | undefined => {
  if (!inputSeparateDate || inputSeparateDate?.length === 0) return;

  return dayjs(
    inputSeparateDate
      ?.map((num, i) => (i !== 0 ? String(num).padStart(2, '0') : String(num)))
      .join('/'),
  ).format(format);
};

/**
 * 日付時刻文字列をフォーマットする汎用関数
 * @param dateTime - 日付時刻文字列（ISO8601形式など）
 * @param format - フォーマット形式（例: 'YYYY/MM/DD'）
 * @returns フォーマット済みの日付文字列
 * @throws 無効な日付形式の場合エラーをthrow
 */
export const formatDate = (dateTime: string, format: string): string => {
  const date = dayjs(dateTime);
  if (!date.isValid()) {
    throw new Error('Invalid date format');
  }
  return date.format(format);
};

/**
 * 郵便番号のフォーマット
 * @param string
 * @returns
 */
export const formatZipCode = (zipCode: string): string => {
  if (!zipCode || zipCode.length !== 7) return zipCode;
  return `〒${zipCode.slice(0, 3)}-${zipCode.slice(3)}`;
};

/**
 * カード番号を桁数に応じてフォーマット
 * @param value カード番号の文字列
 * @returns フォーマットされたカード番号
 */
export const formatCardNumber = (value: string): string => {
  // 値が空の場合は空文字を返す
  if (!value) return '';
  // 数字以外を除去
  const numbersOnly = value.replace(/\D/g, '');
  const length = numbersOnly.length;
  if (length <= 4) {
    return numbersOnly;
  } else if (length <= 13) {
    // 13桁以下は4桁区切り
    return numbersOnly.replace(/(\d{4})(?=\d)/g, '$1 ');
  } else if (length === 14) {
    // 14桁の場合は4,6,4区切り
    return numbersOnly.replace(/(\d{4})(\d{6})(\d{4})/, '$1 $2 $3');
  } else if (length === 15) {
    // 15桁の場合は4,6,5区切り
    return numbersOnly.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  } else {
    // 16桁の場合は4桁区切り
    return numbersOnly.replace(/(\d{4})(?=\d)/g, '$1 ');
  }
};
